import { useCallback, useEffect, useState } from "react";
import type { SimulationInput } from "@/components/rentalmnp/lib/types";

const KEY = "rentalmnp:simulations";
const MAX_SIMULATIONS = 20;

export interface SavedSimulation {
  id: string;
  name: string;
  createdAt: number;
  input: SimulationInput;
}

function read(): SavedSimulation[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as SavedSimulation[];
  } catch {
    return [];
  }
}

function write(list: SavedSimulation[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_SIMULATIONS)));
  } catch {
    /* no-op (quota) */
  }
}

export function useLocalSimulations() {
  const [simulations, setSimulations] = useState<SavedSimulation[]>([]);

  useEffect(() => {
    setSimulations(read());
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setSimulations(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const refresh = useCallback(() => setSimulations(read()), []);

  const save = useCallback(
    (input: SimulationInput, name?: string): SavedSimulation => {
      const list = read();
      const sim: SavedSimulation = {
        id: `sim_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name:
          (name ?? "").trim() ||
          `Simulation du ${new Date().toLocaleDateString("fr-FR")} ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`,
        createdAt: Date.now(),
        input,
      };
      const next = [sim, ...list].slice(0, MAX_SIMULATIONS);
      write(next);
      setSimulations(next);
      return sim;
    },
    []
  );

  const remove = useCallback((id: string) => {
    const next = read().filter((s) => s.id !== id);
    write(next);
    setSimulations(next);
  }, []);

  const load = useCallback((id: string): SavedSimulation | undefined => {
    return read().find((s) => s.id === id);
  }, []);

  const clearAll = useCallback(() => {
    write([]);
    setSimulations([]);
  }, []);

  return { simulations, save, remove, load, refresh, clearAll };
}
